import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Pause, Play, XCircle, Download, Send, Loader2, CalendarClock } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { StatusBadge, fmtDate, inputCls } from "../admin/AFields";
import { Modal } from "../admin/Participants";

const TABS = [["", "All Campaigns"], ["draft", "Drafts"], ["scheduled", "Scheduled"], ["sending", "Sending"], ["completed", "Completed"]];

export default function StudioCampaigns() {
  const [tab, setTab] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [schedEdit, setSchedEdit] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/wa/campaigns${tab ? `?status=${tab}` : ""}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [tab]); // eslint-disable-line

  const openDetail = async (id) => {
    try { const r = await api.get(`/wa/campaigns/${id}`); setOpen(r.data); setSchedEdit(r.data.scheduled_at ? String(r.data.scheduled_at).slice(0, 16) : ""); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const action = async (id, act, extra = {}) => {
    setBusy(act + id);
    try {
      await api.post(`/wa/campaigns/${id}/action`, { action: act, ...extra });
      toast.success(`Campaign ${act}ed`.replace("scheduleed", "scheduled"));
      if (open?.id === id) await openDetail(id);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const report = async (c) => {
    try {
      const r = await api.get(`/wa/campaigns/${c.id}/report`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `campaign-${c.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const counts = (c) => c.counts || {};

  return (
    <div data-testid="studio-campaigns">
      <SEO title="Campaigns" />
      <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">WhatsApp Campaigns & Delivery Reports</h1>
      <p className="mt-1 text-sm text-slate-500">Statuses update automatically from the delivery webhook. One recipient never receives the same campaign twice.</p>

      <div className="mt-5 flex gap-2">
        {TABS.map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`camp-tab-${v || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === v ? "bg-navy text-white" : "border border-gray-200 bg-white text-navy"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-5 space-y-3">
        {items.map((c) => (
          <div key={c.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`camp-row-${c.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-bold text-navy">{c.name} {c.auto ? <span className="ml-1 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600">AUTO</span> : null}</p>
                <p className="mt-0.5 text-xs text-slate-500">{c.occasion || "—"} · sender: {c.sender_org || c.sender_name} · template: {c.template_name} · {fmtDate(c.created_at)}</p>
                {c.scheduled_at && <p className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-saffron-600"><CalendarClock size={12} /> {new Date(c.scheduled_at).toLocaleString()}</p>}
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge value={c.status} />
                <button onClick={() => openDetail(c.id)} className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy hover:border-saffron-500" data-testid={`camp-view-${c.id}`}><Eye size={12} className="mr-1 inline" /> Report</button>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-bold">
              <span className="rounded-full bg-navy-50 px-3 py-1 text-navy">TOTAL {c.recipient_count}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-600">SENT {counts(c).sent || 0}</span>
              <span className="rounded-full bg-brandgreen-100 px-3 py-1 text-brandgreen">DELIVERED {counts(c).delivered || 0}</span>
              <span className="rounded-full bg-brandgreen-100 px-3 py-1 text-brandgreen">READ {counts(c).read || 0}</span>
              <span className="rounded-full bg-red-50 px-3 py-1 text-red-500">FAILED {counts(c).failed || 0}</span>
              <span className="rounded-full bg-saffron-100 px-3 py-1 text-saffron-600">PENDING {counts(c).pending || 0}</span>
              {c.skipped > 0 && <span className="rounded-full bg-slate-100 px-3 py-1 text-slate-500">SKIPPED {c.skipped}</span>}
              <span className="ml-auto flex gap-1.5">
                {["draft", "paused"].includes(c.status) && (
                  <button onClick={() => action(c.id, "send")} disabled={busy} className="rounded-md bg-brandgreen-100 px-3 py-1.5 text-brandgreen disabled:opacity-50" data-testid={`camp-send-${c.id}`}><Send size={12} className="mr-1 inline" /> Send</button>
                )}
                {["sending", "scheduled"].includes(c.status) && (
                  <button onClick={() => action(c.id, "pause")} disabled={busy} className="rounded-md bg-saffron-100 px-3 py-1.5 text-saffron-600 disabled:opacity-50" data-testid={`camp-pause-${c.id}`}><Pause size={12} className="mr-1 inline" /> Pause</button>
                )}
                {c.status === "paused" && (
                  <button onClick={() => action(c.id, "resume")} disabled={busy} className="rounded-md bg-navy-50 px-3 py-1.5 text-navy disabled:opacity-50" data-testid={`camp-resume-${c.id}`}><Play size={12} className="mr-1 inline" /> Resume</button>
                )}
                {!["completed", "cancelled"].includes(c.status) && (
                  <button onClick={() => { if (window.confirm("Cancel this campaign? Pending messages will not be sent.")) action(c.id, "cancel"); }} disabled={busy} className="rounded-md bg-red-50 px-3 py-1.5 text-red-500 disabled:opacity-50" data-testid={`camp-cancel-${c.id}`}><XCircle size={12} className="mr-1 inline" /> Cancel</button>
                )}
                <button onClick={() => report(c)} className="rounded-md border border-gray-200 px-3 py-1.5 text-navy" data-testid={`camp-report-${c.id}`}><Download size={12} className="mr-1 inline" /> CSV</button>
              </span>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="rounded-lg border border-dashed border-gray-300 py-12 text-center text-sm text-slate-400">No campaigns yet — use WhatsApp Sender to create one.</p>}
      </div>

      {open && (
        <Modal onClose={() => setOpen(null)} title={`Campaign — ${open.name}`} testid="camp-detail-modal" wide>
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge value={open.status} />
            <span className="text-xs text-slate-500">{open.recipient_count} recipients · template {open.template_name}</span>
          </div>
          {["draft", "scheduled", "paused"].includes(open.status) && (
            <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-navy-50 p-3">
              <input type="datetime-local" value={schedEdit} onChange={(e) => setSchedEdit(e.target.value)} className={`${inputCls} !w-56`} data-testid="camp-schedule-edit" />
              <button onClick={() => action(open.id, "schedule", { scheduled_at: new Date(schedEdit).toISOString() })} disabled={!schedEdit || busy} className="btn-dark !rounded-md !px-4 !py-2 text-xs disabled:opacity-50" data-testid="camp-schedule-save">Save Schedule</button>
            </div>
          )}
          <div className="mt-4 max-h-96 overflow-y-auto rounded-md border border-gray-100">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-gray-100 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Name</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Attempts</th><th className="px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {(open.messages || []).map((m) => (
                  <tr key={m.id} className="border-b border-gray-50" data-testid={`camp-msg-${m.id}`}>
                    <td className="px-3 py-2 font-semibold text-navy">{m.name || "—"}</td>
                    <td className="px-3 py-2">{m.phone}</td>
                    <td className="px-3 py-2"><StatusBadge value={m.status} /></td>
                    <td className="px-3 py-2 text-xs">{m.attempts}</td>
                    <td className="max-w-[200px] truncate px-3 py-2 text-xs text-red-500">{m.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}
